import { AxiosResponse } from 'axios';

import axiosInstance from 'api/base';
import { normalizeCustomParameters } from 'routes/pages/AuthorizedStack/Clients/ClientsList/Client/Form/helpers';
import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IBaseGist, IPageType, TObject } from 'utils/types';

import {
  normalizeClient,
  normalizeClientAllowedPM,
  normalizeClientErrors,
  normalizeClientGeneralInfo,
  normalizeClientInstruments,
  normalizeClientPageLimit,
  normalizeClientPriorityRulesets,
  normalizeClientRoutingPaymentInstruments,
  normalizeClientRoutingPriority,
  normalizeClientRoutingStats,
  normalizeClientSavedCardToken,
  normalizeClientsLimits,
  normalizeClientStats,
  normalizePaymentMethod,
  normalizeUniquePaymentInstruments,
} from './normalizers';
import {
  IClient,
  IClientACPM,
  IClientACPMData,
  IClientACPMPayload,
  IClientData,
  IClientForm,
  IClientGeneralInfo,
  IClientInstruments,
  IClientInstrumentsData,
  IClientInstrumentStatusForm,
  IClientPageLimitPayload,
  IClientPageLimits,
  IClientPageLimitsData,
  IClientPaymentMethod,
  IClientPriorityRuleset,
  IClientPriorityRulesetData,
  IClientPriorityRulesetPayload,
  IClientRoutingPaymentInstrument,
  IClientRoutingPriority,
  IClientRoutingPriorityData,
  IClientRoutingStats,
  IClientSavedCardToken,
  IClientSavedCardTokenDeletePayload,
  IClientsLimits,
  IClientsLimitsPayload,
  IClientStats,
  IClientTopErrors,
  ICreateClientRoutingPrioritiesPayload,
  IEditClientPayload,
  IEditClientRoutingPriorityPayload,
  IPaymentInstrument,
  IPaymentInstrumentPayload,
  IUpdatePaymentInstrumentPayload,
} from './types';

const clientsAPI = {
  async getAllClients(filters: IAllPageFilters): Promise<IPageType<IClient>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('customers', {
      params: normalizedFilter,
    });
    return normalizePageData<IClientData, IClient>(
      response.data,
      normalizeClient,
      JSON.stringify(filters.search),
    );
  },

  async getClientsGeneralInfo(id: string): Promise<IClientGeneralInfo> {
    const response = await axiosInstance.get(`customers/${id}`);
    return normalizeClientGeneralInfo(response.data);
  },

  async getClientStats(id: string): Promise<IClientStats> {
    const response = await axiosInstance.get(`customers/${id}/stats`);
    return normalizeClientStats(response.data);
  },

  async getClientTopErrors(id: string): Promise<IClientTopErrors> {
    const response = await axiosInstance.get(`customers/${id}/errors`);
    return normalizeClientErrors(response.data);
  },

  async getClientPaymentMethod(id: string): Promise<IClientPaymentMethod> {
    const response = await axiosInstance.get(`customers/${id}/methods`);
    return normalizePaymentMethod(response.data);
  },

  async createClient(payload: IClientForm): Promise<IClientGeneralInfo> {
    const normalizedClient = prepareFormData({
      ...payload,
      customParams: normalizeCustomParameters(payload.customParams),
    });

    const result = await axiosInstance.post(`customers`, normalizedClient);
    return normalizeClientGeneralInfo(result.data);
  },

  async editClient({ id, payload }: IEditClientPayload): Promise<IClientGeneralInfo> {
    const normalizedClient = prepareFormData({
      ...payload,
      customParams: normalizeCustomParameters(payload.customParams),
    });

    const result = await axiosInstance.put(`customers/${id}`, normalizedClient);
    return normalizeClientGeneralInfo(result.data);
  },

  async getClientPageLimits(
    filters: IAllPageFilters,
  ): Promise<IPageType<IClientPageLimits>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('customer/cashier-page-limits', {
      params: normalizedFilter,
    });
    return normalizePageData<IClientPageLimitsData, IClientPageLimits>(
      response.data,
      normalizeClientPageLimit,
      JSON.stringify(filters.search),
    );
  },

  async getClientPageLimit(id: number): Promise<IClientPageLimits> {
    const response = await axiosInstance.get(`customer/cashier-page-limits/${id}`);
    return normalizeClientPageLimit(response.data);
  },

  async createClientPageLimit({
    id,
    payload,
  }: IClientPageLimitPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.post(
      `customer/${id}/cashier-page-limits`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async editClientPageLimit({
    id,
    payload,
  }: IClientPageLimitPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.put(
      `customer/cashier-page-limits/${id}`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async deleteClientLimit(id: number): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(`customer/cashier-page-limits/${id}`);

    return result;
  },

  async getClientAllowedCashierPMs(
    id: string,
    filters: IAllPageFilters,
  ): Promise<IPageType<IClientACPM>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get(`customers/${id}/allowed-cashier-methods`, {
      params: normalizedFilter,
    });

    return normalizePageData<IClientACPMData, IClientACPM>(
      response.data,
      normalizeClientAllowedPM,
      JSON.stringify(filters.search),
    );
  },

  async createClientAllowedCashierPM({
    id,
    payload,
  }: IClientACPMPayload): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.post(
      `customers/${id}/allowed-cashier-methods`,
      normalizedPayload,
    );

    return response;
  },

  async deleteClientAllowedCashierPM(id: number): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(`customers/allowed-cashier-methods/${id}`);

    return result;
  },

  async getClientPriorityRulesets(
    id: string,
    filters: IAllPageFilters,
  ): Promise<IPageType<IClientPriorityRuleset>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get(`customers/${id}/priority-rulesets`, {
      params: normalizedFilter,
    });

    return normalizePageData<IClientPriorityRulesetData, IClientPriorityRuleset>(
      response.data,
      normalizeClientPriorityRulesets,
      JSON.stringify(filters.search),
    );
  },

  async createClientPriorityRulesets({
    id,
    payload,
  }: IClientPriorityRulesetPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.post(
      `customers/${id}/priority-rulesets`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async editClientPriorityRulesets({
    id,
    payload,
  }: IClientPriorityRulesetPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.put(
      `customers/rulesets/${id}/priority-rulesets`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async deleteClientPriorityRulesets(id: number): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(
      `customers/rulesets/${id}/priority-rulesets`,
    );

    return result;
  },

  async getClientRoutingPriorities(
    id: string,
    filters: IAllPageFilters,
  ): Promise<IPageType<IClientRoutingPriority>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get(`customers/${id}/routing-priorities`, {
      params: normalizedFilter,
    });

    return normalizePageData<IClientRoutingPriorityData, IClientRoutingPriority>(
      response.data,
      normalizeClientRoutingPriority,
      JSON.stringify(filters.search),
    );
  },

  async createClientRoutingPriorities({
    id,
    payload,
  }: ICreateClientRoutingPrioritiesPayload): Promise<AxiosResponse> {
    const result = await axiosInstance.post(
      `customers/${id}/routing-priorities`,
      payload,
    );

    return result;
  },

  async editClientRoutingPriority({
    id,
    payload,
  }: IEditClientRoutingPriorityPayload): Promise<AxiosResponse> {
    const result = await axiosInstance.put(`customers/routing-priorities/${id}`, payload);

    return result;
  },

  async deleteClientRoutingPriority(id: number): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(`customers/routing-priorities/${id}`);

    return result;
  },

  async getClientsLimits(id: number | string): Promise<IClientsLimits> {
    const response = await axiosInstance.get(`customers/${id}/limits`);

    return normalizeClientsLimits(response.data);
  },

  async createClientsLimits({
    clientId,
    payload,
  }: IClientsLimitsPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.post(
      `customers/${clientId}/limits`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async editClientsLimits({
    clientId,
    payload,
  }: IClientsLimitsPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.put(
      `customers/${clientId}/limits`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async deleteClientsLimits(clientId: string): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`/customers/${clientId}/limits`);

    return response.data;
  },

  async getUniquePaymentInstruments({
    id,
    transactionType,
  }: IPaymentInstrumentPayload): Promise<IPaymentInstrument[]> {
    const response = await axiosInstance.get(
      `customers/${id}/unique-payment-instruments`,
      {
        params: { transactionType },
      },
    );

    return normalizeUniquePaymentInstruments(response.data);
  },

  async updateUniquePaymentInstrument({
    id,
    settings,
  }: IUpdatePaymentInstrumentPayload): Promise<AxiosResponse> {
    const normalizedSettings = prepareFormData(settings);

    const response = await axiosInstance.patch(
      `customers/unique-payment-instruments/${id}`,
      null,
      {
        params: normalizedSettings,
      },
    );

    return response.data;
  },

  async getClientRoutingStats(id: string): Promise<IClientRoutingStats> {
    const response = await axiosInstance.get(`customers/${id}/routing/stats`);
    return normalizeClientRoutingStats(response.data);
  },

  async getClientRoutingPaymentInstruments({
    id,
    pageFilters,
  }: {
    id: string;
    pageFilters: IAllPageFilters;
  }): Promise<IPageType<IClientRoutingPaymentInstrument>> {
    const normalizedFilter = normalizeSearchFiltersPayload(pageFilters);

    const response = await axiosInstance.get(
      `customers/${id}/routing/stats/instruments`,
      {
        params: normalizedFilter,
      },
    );
    return normalizePageData<TObject, IClientRoutingPaymentInstrument>(
      response.data,
      normalizeClientRoutingPaymentInstruments,
      JSON.stringify(pageFilters.search),
    );
  },

  async getClientSavedCardTokens({
    id,
    pageFilters,
  }: {
    id: string;
    pageFilters: IAllPageFilters;
  }): Promise<IPageType<IClientSavedCardToken>> {
    const normalizedFilter = normalizeSearchFiltersPayload(pageFilters);

    const response = await axiosInstance.get(`customers/${id}/card-tokens`, {
      params: normalizedFilter,
    });
    return normalizePageData<TObject, IClientSavedCardToken>(
      response.data,
      normalizeClientSavedCardToken,
      JSON.stringify(pageFilters.search),
    );
  },

  async deleteClientSavedCardTokens({
    cardPanId,
    customerId,
  }: IClientSavedCardTokenDeletePayload): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(`customers/${customerId}/card-tokens`, {
      params: { cardPanId },
    });

    return result;
  },

  async recalculateClientStats(id: number | string): Promise<AxiosResponse> {
    const result = await axiosInstance.post(`customers/${id}/stats/recalculate`);

    return result;
  },

  async getAllClientsInstruments(
    filters: IAllPageFilters,
  ): Promise<IPageType<IClientInstruments>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('customers/card-tokens', {
      params: normalizedFilter,
    });
    return normalizePageData<IClientInstrumentsData, IClientInstruments>(
      response.data,
      normalizeClientInstruments,
      JSON.stringify(filters.search),
    );
  },

  async updateClientInstrument({
    customerId,
    ...payload
  }: IClientInstrumentStatusForm): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const result = await axiosInstance.post(
      `customers/${customerId}/card-tokens`,
      normalizedPayload,
    );
    return normalizeBaseGist(result?.data);
  },
};

export default clientsAPI;
